import Microgame from '../engine/Microgame.js';

export default class SnatchGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Arrache !`, en: `Snatch!`, es: `¡Arrebata!` },
            controls: { fr: `Appuie très vite (ou glisse) pour attraper en premier`, en: `Tap fast (or swipe) to grab it first`, es: `Toca rápido (o desliza) para atraparlo primero` },
            objective: { fr: `Sois plus vif que ton adversaire pour récupérer l'objet`, en: `Be faster than your opponent to get the object`, es: `Sé más rápido que tu oponente para obtener el objeto` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><path d="M19 14.5v-3c0-2-1.5-3.5-3.5-3.5h-7c-2 0-3.5 1.5-3.5 3.5v3"/><path d="m14 11-4 4 4 4"/><circle cx="12" cy="12" r="10"/></svg>`,
            actions: [
                { type: 'wait', duration: 900 },
                { type: 'tap', x: 50, y: 72, duration: 140 },
                { type: 'wait', duration: 360 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.hand = null;
        this.object = null;
        this.objectGlow = null;
        this.state = 'waiting'; // waiting, dropping, caught, missed
        this.dropDelay = 0;
        this.objectY = -20; // %
        this.dropSpeed = 0;

        // Soften difficulty: map speedMult 1->4 to effective 1->2.5
        const speedMult = engine.speedMultiplier || 1.0;
        const effectiveMult = 1.0 + (speedMult - 1) * (1.5 / 3);
        this.speedFactor = effectiveMult;

        // Generous duration
        const adjustedSpeed = 1 + (speedMult - 1) * (1.2 / 3);
        this.duration = 4000 * (speedMult / adjustedSpeed);
    }

    setup() {
        this.engine.ui.instruction.textContent = "GRAB!";

        // Background ambiance: subtle radial gradient
        this.container.style.background = 'radial-gradient(ellipse at 50% 30%, rgba(255,255,255,0.03) 0%, transparent 70%)';

        // Decorative drop zone indicator (subtle dashed lines on sides)
        const dropZone = document.createElement('div');
        dropZone.style.cssText = `
            position: absolute; top: 0; left: 50%; transform: translateX(-50%);
            width: 80px; height: 100%; 
            border-left: 1px dashed rgba(255,255,255,0.08);
            border-right: 1px dashed rgba(255,255,255,0.08);
            pointer-events: none;
        `;
        this.container.appendChild(dropZone);

        // Hand (Secondary) - Stylized open palm
        this.hand = document.createElement('div');
        this.hand.style.cssText = `
            position: absolute; width: 110px; height: 110px;
            border: 4px solid ${this.secondaryColor};
            border-top: none;
            border-radius: 0 0 55% 55%;
            bottom: 18%; left: 50%; transform: translateX(-50%);
            box-shadow: 0 4px 20px ${this.secondaryColor}44, inset 0 -10px 30px ${this.secondaryColor}22;
            transition: height 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
        `;
        this.container.appendChild(this.hand);

        // Fingers (3 small arcs on top of the hand)
        for (let i = 0; i < 3; i++) {
            const finger = document.createElement('div');
            const offset = (i - 1) * 30;
            finger.style.cssText = `
                position: absolute; width: 18px; height: 28px;
                border: 3px solid ${this.secondaryColor};
                border-bottom: none;
                border-radius: 10px 10px 0 0;
                top: -28px; left: calc(50% + ${offset}px); transform: translateX(-50%);
                opacity: 0.7;
                transition: opacity 0.15s ease, transform 0.15s ease;
            `;
            finger.classList.add('finger');
            this.hand.appendChild(finger);
        }

        // Object (Primary) - Glowing card/bill with inner details
        this.object = document.createElement('div');
        this.object.style.cssText = `
            position: absolute; width: 55px; height: 90px;
            background: linear-gradient(135deg, ${this.primaryColor}, ${this.primaryColor}cc);
            top: -20%; left: 50%; transform: translateX(-50%);
            border-radius: 6px;
            box-shadow: 0 0 15px ${this.primaryColor}88, 0 0 40px ${this.primaryColor}33;
            transition: transform 0.15s ease;
        `;
        this.container.appendChild(this.object);

        // Inner detail on the object (like a bill marking)
        const innerMark = document.createElement('div');
        innerMark.style.cssText = `
            position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
            width: 30px; height: 30px;
            border: 2px solid rgba(255,255,255,0.3);
            border-radius: 50%;
        `;
        this.object.appendChild(innerMark);

        const innerDot = document.createElement('div');
        innerDot.style.cssText = `
            position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
            width: 8px; height: 8px;
            background: rgba(255,255,255,0.4);
            border-radius: 50%;
        `;
        innerMark.appendChild(innerDot);

        // Stripe lines on the object
        for (let i = 0; i < 2; i++) {
            const stripe = document.createElement('div');
            stripe.style.cssText = `
                position: absolute; left: 10%; right: 10%;
                height: 2px; background: rgba(255,255,255,0.15);
                top: ${i === 0 ? '15%' : '85%'};
                border-radius: 1px;
            `;
            this.object.appendChild(stripe);
        }

        // Pulsing glow beneath the object
        this.objectGlow = document.createElement('div');
        this.objectGlow.style.cssText = `
            position: absolute; width: 70px; height: 20px;
            background: ${this.primaryColor}44;
            border-radius: 50%;
            filter: blur(10px);
            left: 50%; transform: translateX(-50%);
            top: -18%; pointer-events: none;
            animation: snatchPulse 1.2s ease-in-out infinite alternate;
        `;
        this.container.appendChild(this.objectGlow);

        // Add pulse animation
        const style = document.createElement('style');
        style.textContent = `
            @keyframes snatchPulse {
                0% { opacity: 0.4; transform: translateX(-50%) scale(0.9); }
                100% { opacity: 1; transform: translateX(-50%) scale(1.1); }
            }
        `;
        this.container.appendChild(style);

        // Random drop delay (0.3 to 1.2s) - slightly more generous
        this.dropDelay = this.isPlayback ? 0.55 : 0.3 + Math.random() * 0.9;

        this.onDown = this.onDown.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onDown);
    }

    update(dt) {
        if (!this.active || this.won) return;

        if (this.state === 'waiting') {
            this.dropDelay -= dt / 1000;
            if (this.dropDelay <= 0) {
                this.state = 'dropping';
                // Softened drop speed: base 600, scales gently with difficulty
                this.dropSpeed = 600 * this.speedFactor;
            }
        } else if (this.state === 'dropping') {
            this.objectY += this.dropSpeed * (dt / 1000) / (this.container.clientHeight / 100);
            this.object.style.top = `${this.objectY}%`;
            this.objectGlow.style.top = `${this.objectY}%`;

            // Missed (fell through)
            if (this.objectY > 100) {
                this.state = 'missed';
                this.lose();
            }
        } else if (this.state === 'missed_anim') {
            // Continue falling
            this.objectY += this.dropSpeed * (dt / 1000) / (this.container.clientHeight / 100);
            this.object.style.top = `${this.objectY}%`;
            this.objectGlow.style.top = `${this.objectY}%`;
        }
    }

    onDown() {
        if (!this.active || this.state !== 'dropping') return;

        // Check overlap
        const hRect = this.hand.getBoundingClientRect();
        const oRect = this.object.getBoundingClientRect();

        // Check vertical overlap
        if (oRect.bottom > hRect.top && oRect.top < hRect.bottom) {
            // CAUGHT!
            this.state = 'caught';
            this.hideTimer = true;

            // Visuals: Object snaps into hand
            this.object.style.top = 'auto';
            this.object.style.bottom = '23%';
            this.object.style.transform = 'translateX(-50%) rotate(8deg) scale(0.9)';
            this.object.style.boxShadow = `0 0 25px ${this.primaryColor}, 0 0 60px ${this.primaryColor}66`;

            // Hand closes with satisfying visual
            this.hand.style.height = '65px';
            this.hand.style.background = `${this.secondaryColor}33`;
            this.hand.style.boxShadow = `0 4px 30px ${this.secondaryColor}88, inset 0 -10px 30px ${this.secondaryColor}44`;

            // Fold fingers
            this.hand.querySelectorAll('.finger').forEach(f => {
                f.style.opacity = '0';
                f.style.transform = 'translateX(-50%) scaleY(0)';
            });

            // Hide glow
            this.objectGlow.style.display = 'none';

            // Delay win
            setTimeout(() => {
                if (this.active) this.win();
            }, 500);

        } else {
            // MISSED (Too early)
            this.state = 'missed_anim';

            // Hand closes empty
            this.hand.style.height = '65px';
            this.hand.style.background = `${this.secondaryColor}22`;

            // Fold fingers
            this.hand.querySelectorAll('.finger').forEach(f => {
                f.style.opacity = '0';
                f.style.transform = 'translateX(-50%) scaleY(0)';
            });

            // Delay lose
            setTimeout(() => {
                if (this.active) this.lose();
            }, 500);
        }
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
    }
}
